import { css, cx } from '@emotion/css';
import { useState, useEffect, type HTMLAttributes } from 'react';
import { User, Mail, Phone, FileText } from 'lucide-react';
import { useAuth } from '../../../auth/hooks/use-auth';
import type { UserInfo } from '../../../../api/services/orders/types';
import { GoogleButton } from '../../../auth/components/google-button/google-button';
import { FacebookButton } from '../../../auth/components/facebook-button/facebook-button';
import { useLocation } from 'react-router-dom';

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
});

const title = css({
  fontSize: 'var(--fs-500)',
  fontWeight: 600,
  color: 'var(--pc-800)',
  marginBottom: 'var(--spacing-md)',
});

const description = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-700)',
  marginBottom: 'var(--spacing-lg)',
  lineHeight: 1.6,
});

const formRow = css({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 'var(--spacing-md)',
  '@media (min-width: 768px)': {
    gridTemplateColumns: '1fr 1fr',
  },
});

const formGroup = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
});

const label = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-700)',
  fontWeight: 500,
});

const inputWrapper = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
});

const icon = css({
  position: 'absolute',
  left: 'var(--spacing-sm)',
  width: 'var(--fs-500)',
  height: 'var(--fs-500)',
  color: 'var(--g-400)',
});

const input = css({
  width: '100%',
  padding: 'var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) calc(var(--spacing-xl) + var(--spacing-xs))',
  fontSize: 'var(--fs-350)',
  border: '1px solid var(--g-300)',
  borderRadius: 'var(--spacing-xs)',
  outline: 'none',
  transition: 'all 0.2s ease',
  '&:focus': {
    borderColor: 'var(--pc-600)',
    boxShadow: '0 0 0 2px rgba(var(--pcrgba-600), 0.2)',
  },
  '&::placeholder': {
    color: 'var(--g-400)',
  },
  '&:disabled': {
    backgroundColor: 'var(--g-100)',
    cursor: 'not-allowed',
  },
});

const helperText = css({
  fontSize: 'var(--fs-300)',
  color: 'var(--g-600)',
  marginTop: 'var(--spacing-xs)',
});

const separator = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-md)',
  margin: 'var(--spacing-lg) 0',
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: '1px',
    backgroundColor: 'var(--g-300)',
  },
});

const separatorText = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-600)',
  fontWeight: 500,
  whiteSpace: 'nowrap',
});

const socialButtonsContainer = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  '@media (min-width: 768px)': {
    flexDirection: 'row',
  },
});

const socialButton = css({
  flex: 1,
});

// Format helpers
const formatPhone = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 10) {
    // (XX) XXXX-XXXX
    return cleaned
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  } else {
    // (XX) XXXXX-XXXX
    return cleaned
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }
};

const formatDocument = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  // CPF: XXX.XXX.XXX-XX
  if (cleaned.length <= 11) {
    return cleaned
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  // CNPJ: XX.XXX.XXX/XXXX-XX
  return cleaned
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

export interface UserInfoFormData extends UserInfo {
  isValid: boolean;
}

export interface CheckoutUserInfoFormProps extends HTMLAttributes<HTMLDivElement> {
  existingUserInfo?: Partial<UserInfo>;
  onDataChange?: (data: UserInfoFormData) => void;
}

export function CheckoutUserInfoForm({
  existingUserInfo,
  onDataChange,
  className,
  style,
  ...props
}: CheckoutUserInfoFormProps) {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  // Initialize with existing data or user profile data
  const [name, setName] = useState(existingUserInfo?.name || user?.name || '');
  const [email, setEmail] = useState(existingUserInfo?.email || user?.email || '');
  const [phone, setPhone] = useState(existingUserInfo?.phone || user?.phone || '');
  const [document, setDocument] = useState(existingUserInfo?.document || user?.cpf || '');

  // Track if fields are from user profile (to disable editing)
  const isEmailFromProfile = isAuthenticated && !!user?.email;
  const isNameFromProfile = isAuthenticated && !!user?.name;
  const isPhoneFromProfile = isAuthenticated && !!user?.phone;
  const isDocumentFromProfile = isAuthenticated && !!user?.cpf;

  // Build redirect URL to come back to this exact step (same format as login page)
  const redirectUrl = `${window.location.origin}${location.pathname}${location.search}`;

  const validateAndNotify = (data: Partial<UserInfo>) => {
    const currentData: UserInfo = {
      name: data.name ?? name,
      email: data.email ?? email,
      phone: data.phone ?? phone,
      document: data.document ?? document,
    };

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailRegex.test(currentData.email);

    // Validate phone (at least 10 digits)
    const cleanedPhone = currentData.phone.replace(/\D/g, '');
    const isPhoneValid = cleanedPhone.length >= 10;

    // Validate document (CPF: 11 digits or CNPJ: 14 digits)
    const cleanedDocument = currentData.document.replace(/\D/g, '');
    const isDocumentValid = cleanedDocument.length === 11 || cleanedDocument.length === 14;

    // Validate name
    const isNameValid = currentData.name.trim().length > 0;

    const isValid = isEmailValid && isPhoneValid && isDocumentValid && isNameValid;

    onDataChange?.({
      ...currentData,
      isValid,
    });
  };

  // Notify on mount with initial data
  useEffect(() => {
    validateAndNotify({});
  }, []);

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhone(value);
    setPhone(formatted);
    validateAndNotify({ phone: formatted });
  };

  const handleDocumentChange = (value: string) => {
    const formatted = formatDocument(value);
    setDocument(formatted);
    validateAndNotify({ document: formatted });
  };

  return (
    <div className={cx(container, className)} style={style} {...props}>
      <div>
        <h3 className={title}>Informações do Comprador</h3>
        <p className={description}>
          {isAuthenticated
            ? 'Complete suas informações para finalizar a compra. Seus dados já salvos no perfil foram preenchidos automaticamente.'
            : 'Preencha suas informações para continuar com a compra. Você pode criar uma conta após a finalização.'}
        </p>
      </div>

      {/* Name field - Full width - Only show if not from profile */}
      {(!isAuthenticated || !isNameFromProfile) && (
        <div className={formGroup}>
          <label className={label} htmlFor="userName">
            Nome completo *
          </label>
          <div className={inputWrapper}>
            <User className={icon} />
            <input
              type="text"
              id="userName"
              placeholder="Seu nome completo"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                validateAndNotify({ name: e.target.value });
              }}
              disabled={isNameFromProfile}
              className={input}
              required
            />
          </div>
          {isNameFromProfile && (
            <p className={helperText}>
              Este campo foi preenchido com os dados do seu perfil
            </p>
          )}
        </div>
      )}

      {/* Email and Phone - Two columns on desktop */}
      <div className={formRow}>
        {/* Email - Only show if not from profile */}
        {(!isAuthenticated || !isEmailFromProfile) && (
          <div className={formGroup}>
            <label className={label} htmlFor="userEmail">
              E-mail *
            </label>
            <div className={inputWrapper}>
              <Mail className={icon} />
              <input
                type="email"
                id="userEmail"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  validateAndNotify({ email: e.target.value });
                }}
                disabled={isEmailFromProfile}
                className={input}
                required
              />
            </div>
            {isEmailFromProfile && (
              <p className={helperText}>
                Este campo foi preenchido com os dados do seu perfil
              </p>
            )}
          </div>
        )}

        {/* Phone - Only show if not from profile */}
        {(!isAuthenticated || !isPhoneFromProfile) && (
          <div className={formGroup}>
            <label className={label} htmlFor="userPhone">
              Telefone *
            </label>
            <div className={inputWrapper}>
              <Phone className={icon} />
              <input
                type="tel"
                id="userPhone"
                placeholder="(00) 00000-0000"
                value={phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                maxLength={15}
                className={input}
                required
              />
            </div>
            <p className={helperText}>
              Enviaremos confirmação e atualizações
            </p>
          </div>
        )}
      </div>

      {/* Document - Full width - Only show if not from profile */}
      {(!isAuthenticated || !isDocumentFromProfile) && (
        <div className={formGroup}>
          <label className={label} htmlFor="userDocument">
            CPF ou CNPJ *
          </label>
          <div className={inputWrapper}>
            <FileText className={icon} />
            <input
              type="text"
              id="userDocument"
              placeholder="000.000.000-00"
              value={document}
              onChange={(e) => handleDocumentChange(e.target.value)}
              maxLength={18}
              className={input}
              required
            />
          </div>
          <p className={helperText}>
            Necessário para emissão da nota fiscal
          </p>
        </div>
      )}

      {/* Login buttons - Only show for guest users */}
      {!isAuthenticated && (
        <>
          <div className={separator}>
            <span className={separatorText}>ou entre com</span>
          </div>

          <div className={socialButtonsContainer}>
            <GoogleButton className={socialButton} redirectUrl={redirectUrl} />
            <FacebookButton className={socialButton} redirectUrl={redirectUrl} />
          </div>
        </>
      )}
    </div>
  );
}

export default CheckoutUserInfoForm;
